// pages/Admin/SupportManagement.tsx
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../store/store";
import {
  adminGetAllTickets,
  adminGetTicketMessages,
  adminSendMessage,
  adminUpdateStatus,
  setAdminActiveTicket,
  addIncomingMessage,
  updateTicketStatus,
  markAdminTicketRead,
} from "../../store/slices/supportSlice";
import { SupportTicket, SupportMessage } from "../../services/supportService";
import Header from "../../components/Layout/Header";
import BottomNav from "../../components/Layout/BottomNav";
import globalStyle from "../../styles/globalStyle";
import { formatDate } from "../../utils/formatters";
import {
  ChatBubbleLeftRightIcon,
  MagnifyingGlassIcon,
  PaperAirplaneIcon,
  PaperClipIcon,
  XMarkIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  UserCircleIcon,
  ShieldCheckIcon,
  ChevronDownIcon,
  DocumentIcon,
  PhotoIcon,
  FunnelIcon,
  InboxIcon,
} from "@heroicons/react/24/outline";
import { CheckCircleIcon as CheckCircleIconSolid } from "@heroicons/react/24/solid";
import TranslatedMessage from "../../components/Support/TranslatedMessage";

// ─── helpers ────────────────────────────────────────────────────────────────
const priorityColors: Record<string, string> = {
  low: "text-gray-400 bg-gray-800",
  normal: "text-blue-400 bg-blue-900",
  high: "text-orange-400 bg-orange-900",
  urgent: "text-red-400 bg-red-900",
};

const statusConfig: Record<
  string,
  { label: string; color: string; icon: React.ElementType }
> = {
  open: {
    label: "Open",
    color: "text-green-400 bg-green-900/50 border-green-800",
    icon: InboxIcon,
  },
  in_progress: {
    label: "In Progress",
    color: "text-blue-400 bg-blue-900/50 border-blue-800",
    icon: ClockIcon,
  },
  resolved: {
    label: "Resolved",
    color: "text-purple-400 bg-purple-900/50 border-purple-800",
    icon: CheckCircleIcon,
  },
  closed: {
    label: "Closed",
    color: "text-gray-400 bg-gray-800/50 border-gray-700",
    icon: XMarkIcon,
  },
};

const formatMsgTime = (d: string) => {
  const date = new Date(d);
  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();
  return isToday
    ? date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    : date.toLocaleDateString([], {
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
};

// ─── Loading Screen ──────────────────────────────────────────────────────────
const LoadingScreen: React.FC<{ message?: string }> = ({
  message = "Loading...",
}) => (
  <div className="w-full h-full bg-black/80 flex flex-col gap-2 items-center justify-center min-h-[160px]">
    <svg className={`${globalStyle.loadingSpinner} mr-2`} viewBox="0 0 24 24">
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
        fill="none"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
    <div className="text-center">
      <p className="mt-4 text-gray-400">{message}</p>
    </div>
  </div>
);

const AdminSupportManagement: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useSelector((s: RootState) => s.auth);
  const {
    adminTickets,
    adminMessagesByTicket,
    adminActiveTicket,
    adminStats,
    adminUnread,
    isLoading,
    adminIsTyping,
    adminTypingUser,
    adminTypingTicketId,
  } = useSelector((s: RootState) => s.support);

  const [messageText, setMessageText] = useState("");
  const [attachmentFile, setAttachmentFile] = useState<File | null>(null);
  const [attachmentPreview, setAttachmentPreview] = useState<string | null>(
    null,
  );
  const [isSending, setIsSending] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [priorityFilter, setPriorityFilter] = useState<string>("all");
  const [showFilters, setShowFilters] = useState(false);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [typingTimeout, setTypingTimeout] = useState<ReturnType<
    typeof setTimeout
  > | null>(null);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    dispatch(adminGetAllTickets());
  }, [dispatch]);

  useEffect(() => {
    if (!adminActiveTicket) return;
    dispatch(adminGetTicketMessages(adminActiveTicket.id));
    dispatch(markAdminTicketRead(adminActiveTicket.id));
  }, [adminActiveTicket?.id, dispatch]);

  const isClosed =
    adminActiveTicket?.status === "closed" ||
    adminActiveTicket?.status === "resolved";
  const activeMessages: SupportMessage[] = adminActiveTicket
    ? adminMessagesByTicket[adminActiveTicket.id] || []
    : [];

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [activeMessages, adminIsTyping]);

  const filteredTickets = (adminTickets || []).filter((t) => {
    const matchSearch =
      !searchTerm ||
      t.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.user?.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.user?.email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchStatus = statusFilter === "all" || t.status === statusFilter;
    const matchPriority =
      priorityFilter === "all" || t.priority === priorityFilter;
    return matchSearch && matchStatus && matchPriority;
  });

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      alert("File size must be less than 10MB");
      return;
    }
    setAttachmentFile(file);
    if (file.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onload = (ev) => setAttachmentPreview(ev.target?.result as string);
      reader.readAsDataURL(file);
    } else {
      setAttachmentPreview(null);
    }
  };

  const handleTyping = () => {
    if (!adminActiveTicket) return;
    if (typingTimeout) clearTimeout(typingTimeout);
    import("../../services/supportService").then(({ default: svc }) => {
      svc.adminSendTyping(adminActiveTicket.id, true);
    });
    const t = setTimeout(() => {
      setTypingTimeout(null);
      import("../../services/supportService").then(({ default: svc }) => {
        if (adminActiveTicket) svc.adminSendTyping(adminActiveTicket.id, false);
      });
    }, 2000);
    setTypingTimeout(t);
  };

  const handleSend = async () => {
    if (
      (!messageText.trim() && !attachmentFile) ||
      !adminActiveTicket ||
      isSending
    )
      return;
    if (typingTimeout) {
      clearTimeout(typingTimeout);
      setTypingTimeout(null);
    }
    import("../../services/supportService").then(({ default: svc }) => {
      svc.adminSendTyping(adminActiveTicket.id, false);
    });
    setIsSending(true);

    await dispatch(
      adminSendMessage({
        ticketId: adminActiveTicket.id,
        message: messageText.trim() || null,
        attachment: attachmentFile,
      }),
    );

    setMessageText("");
    setAttachmentFile(null);
    setAttachmentPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
    setIsSending(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleStatusUpdate = async (status: string) => {
    if (!adminActiveTicket) return;
    await dispatch(
      adminUpdateStatus({ ticketId: adminActiveTicket.id, status }),
    );
    dispatch(updateTicketStatus({ ticketId: adminActiveTicket.id, status }));
    setShowStatusDropdown(false);
  };

  return (
    <div
      className={globalStyle.container}
      style={{ height: "100dvh", display: "flex", flexDirection: "column" }}
    >
      <Header />

      <div className="flex w-full flex-1 min-h-0 overflow-hidden pb-14">
        {/* ── LEFT PANEL ─────────────────────────────────────────────── */}
        <aside
          className={`${adminActiveTicket ? "hidden md:flex" : "flex"} flex-col flex-shrink-0 w-full md:w-80 lg:w-96 min-w-0 border-r border-gray-800 bg-gray-950 overflow-hidden`}
        >
          <div className="p-4 border-b border-gray-800">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-bold text-white flex items-center gap-2">
                <ChatBubbleLeftRightIcon className="w-5 h-5 text-orange-500" />
                Support Tickets
                {adminUnread > 0 && (
                  <span className="ml-1 px-2 py-0.5 text-xs bg-orange-500 text-black rounded-full font-bold">
                    {adminUnread}
                  </span>
                )}
              </h2>
              <button
                onClick={() => dispatch(adminGetAllTickets())}
                className="p-1.5 rounded-lg hover:bg-gray-800 transition-colors text-gray-400 hover:text-white"
              >
                <ArrowPathIcon className="w-4 h-4" />
              </button>
            </div>

            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search tickets..."
                className="w-full pl-9 pr-9 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-white placeholder-gray-500 focus:outline-none focus:border-orange-500/50"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white"
                >
                  <XMarkIcon className="w-4 h-4" />
                </button>
              )}
            </div>

            <button
              onClick={() => setShowFilters(!showFilters)}
              className="mt-2 flex items-center gap-1.5 text-xs text-gray-400 hover:text-orange-400 transition-colors"
            >
              <FunnelIcon className="w-3.5 h-3.5" />
              Filters
              {(statusFilter !== "all" || priorityFilter !== "all") && (
                <span className="w-2 h-2 rounded-full bg-orange-500" />
              )}
            </button>

            {showFilters && (
              <div className="mt-2 p-3 bg-gray-800 rounded-lg space-y-2">
                <div>
                  <label className="text-xs text-gray-400 block mb-1">
                    Status
                  </label>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="w-full bg-gray-700 border border-gray-600 rounded text-sm text-white py-1 px-2 focus:outline-none focus:border-orange-500"
                  >
                    <option value="all">All</option>
                    <option value="open">Open</option>
                    <option value="in_progress">In Progress</option>
                    <option value="resolved">Resolved</option>
                    <option value="closed">Closed</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs text-gray-400 block mb-1">
                    Priority
                  </label>
                  <select
                    value={priorityFilter}
                    onChange={(e) => setPriorityFilter(e.target.value)}
                    className="w-full bg-gray-700 border border-gray-600 rounded text-sm text-white py-1 px-2 focus:outline-none focus:border-orange-500"
                  >
                    <option value="all">All</option>
                    <option value="low">Low</option>
                    <option value="normal">Normal</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>
              </div>
            )}

            <div className="mt-3 grid grid-cols-4 gap-1">
              {Object.entries(statusConfig).map(([key, cfg]) => {
                const count = (adminTickets || []).filter(
                  (t) => t.status === key,
                ).length;
                return (
                  <button
                    key={key}
                    onClick={() =>
                      setStatusFilter(statusFilter === key ? "all" : key)
                    }
                    className={`flex flex-col items-center py-1.5 rounded-lg border text-xs transition-all ${
                      statusFilter === key
                        ? cfg.color + " font-bold"
                        : "border-gray-800 text-gray-500 hover:border-gray-700"
                    }`}
                  >
                    <span className="text-base font-bold">{count}</span>
                    <span className="capitalize">{cfg.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex-1 overflow-y-auto">
            {isLoading && (adminTickets || []).length === 0 ? (
              <LoadingScreen message="Loading tickets..." />
            ) : filteredTickets.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-40 text-gray-500">
                <InboxIcon className="w-10 h-10 mb-2 opacity-40" />
                <p className="text-sm">No tickets found</p>
              </div>
            ) : (
              filteredTickets.map((ticket) => {
                const isActive = adminActiveTicket?.id === ticket.id;
                const cfg = statusConfig[ticket.status] || statusConfig.open;
                const StatusIcon = cfg.icon;

                return (
                  <button
                    key={ticket.id}
                    onClick={() => dispatch(setAdminActiveTicket(ticket))}
                    className={`w-full text-left p-4 border-b border-gray-800/50 transition-all hover:bg-gray-800/40 ${
                      isActive
                        ? "bg-orange-500/10 border-l-2 border-l-orange-500"
                        : "border-l-2 border-l-transparent"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className="text-xs text-gray-500">
                            #{ticket.id}
                          </span>
                          <span
                            className={`text-xs px-1.5 py-0.5 rounded-full ${
                              priorityColors[ticket.priority] ||
                              priorityColors.normal
                            }`}
                          >
                            {ticket.priority}
                          </span>
                          {(ticket.unread_count ?? 0) > 0 && (
                            <span className="ml-auto text-xs px-1.5 py-0.5 rounded-full bg-orange-500 text-black font-bold">
                              {ticket.unread_count}
                            </span>
                          )}
                        </div>
                        <p className="text-sm font-medium text-white truncate">
                          {ticket.subject}
                        </p>
                        {ticket.latest_message && (
                          <p className="text-xs text-gray-500 truncate mt-0.5">
                            {ticket.latest_message}
                          </p>
                        )}
                        <div className="flex items-center gap-1 mt-1">
                          <UserCircleIcon className="w-3.5 h-3.5 text-gray-500 flex-shrink-0" />
                          <span className="text-xs text-gray-400 truncate">
                            {ticket.user?.username || "Unknown"}
                          </span>
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-1 flex-shrink-0">
                        <span
                          className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border ${cfg.color}`}
                        >
                          <StatusIcon className="w-3 h-3" />
                          {cfg.label}
                        </span>
                        {ticket.last_message_at && (
                          <span className="text-xs text-gray-600">
                            {formatMsgTime(ticket.last_message_at)}
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </aside>

        {/* ── RIGHT PANEL ────────────────────────────────────────────── */}
        <main
          className={`${adminActiveTicket ? "flex" : "hidden md:flex"} flex-1 flex-col min-w-0 overflow-hidden bg-black`}
        >
          {!adminActiveTicket ? (
            <div className="flex-1 flex flex-col items-center justify-center gap-4 p-8">
              <div className="w-24 h-24 rounded-3xl bg-gradient-to-br from-gray-900 to-gray-800 border border-gray-700 flex items-center justify-center shadow-xl">
                <ChatBubbleLeftRightIcon className="w-12 h-12 text-gray-600" />
              </div>
              <div className="text-center">
                <p className="text-lg font-semibold text-gray-400">
                  No ticket selected
                </p>
                <p className="text-sm text-gray-600 mt-1">
                  Pick a conversation from the left to get started
                </p>
              </div>
              <div className="flex gap-6 mt-2 text-center">
                {Object.entries(statusConfig).map(([key, cfg]) => {
                  const count = (adminTickets || []).filter(
                    (t) => t.status === key,
                  ).length;
                  const Icon = cfg.icon;
                  return (
                    <div key={key} className="flex flex-col items-center gap-1">
                      <div
                        className={`w-10 h-10 rounded-xl border flex items-center justify-center ${cfg.color}`}
                      >
                        <Icon className="w-5 h-5" />
                      </div>
                      <span className="text-lg font-bold text-white">
                        {count}
                      </span>
                      <span className="text-xs text-gray-500">{cfg.label}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <>
              {/* ── Chat Header ─────────────────────────────────────── */}
              <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-800 bg-gray-950 flex-shrink-0">
                <button
                  onClick={() => dispatch(setAdminActiveTicket(null))}
                  className="md:hidden p-1.5 rounded-lg hover:bg-gray-800 text-gray-400 hover:text-white transition-colors flex-shrink-0"
                >
                  ←
                </button>

                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-gray-700 to-gray-600 flex items-center justify-center flex-shrink-0 text-sm font-bold text-white">
                  {adminActiveTicket.user?.username?.[0]?.toUpperCase() ?? "U"}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-semibold text-white truncate">
                      {adminActiveTicket.user?.username ?? "Unknown User"}
                    </p>
                    <span className="text-xs text-gray-600">·</span>
                    <p className="text-xs text-gray-400 truncate">
                      {adminActiveTicket.user?.email}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 mt-0.5">
                    <p className="text-xs text-gray-500 truncate">
                      #{adminActiveTicket.id} · {adminActiveTicket.subject}
                    </p>
                    <span
                      className={`inline-flex text-xs px-1.5 py-0.5 rounded-full flex-shrink-0 ${priorityColors[adminActiveTicket.priority] || priorityColors.normal}`}
                    >
                      {adminActiveTicket.priority}
                    </span>
                  </div>
                </div>

                <div className="relative flex-shrink-0">
                  <button
                    onClick={() => setShowStatusDropdown(!showStatusDropdown)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-semibold transition-all hover:opacity-80 ${statusConfig[adminActiveTicket.status]?.color ?? "border-gray-700 text-gray-400"}`}
                  >
                    {statusConfig[adminActiveTicket.status]?.label ??
                      adminActiveTicket.status}
                    <ChevronDownIcon className="w-3 h-3" />
                  </button>

                  {showStatusDropdown && (
                    <>
                      <div
                        className="fixed inset-0 z-40"
                        onClick={() => setShowStatusDropdown(false)}
                      />
                      <div className="absolute right-0 top-full mt-2 w-44 bg-gray-900 border border-gray-700 rounded-2xl shadow-2xl z-50 overflow-hidden py-1">
                        {Object.entries(statusConfig).map(([key, cfg]) => {
                          const Icon = cfg.icon;
                          const isActive = adminActiveTicket.status === key;
                          return (
                            <button
                              key={key}
                              onClick={() => handleStatusUpdate(key)}
                              className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-sm transition-colors ${isActive ? "bg-orange-500/10 text-orange-400" : "text-gray-300 hover:bg-gray-800"}`}
                            >
                              <Icon className="w-4 h-4 flex-shrink-0" />
                              {cfg.label}
                              {isActive && (
                                <CheckCircleIconSolid className="w-4 h-4 ml-auto text-orange-400" />
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* ── Messages ─────────────────────────────────────────── */}
              <div className="flex-1 overflow-y-auto px-4 py-4 space-y-1">
                {isLoading && activeMessages.length === 0 ? (
                  <LoadingScreen message="Loading messages..." />
                ) : activeMessages.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full gap-3 text-gray-600">
                    <div className="w-14 h-14 rounded-2xl bg-gray-900 border border-gray-800 flex items-center justify-center">
                      <ChatBubbleLeftRightIcon className="w-7 h-7 text-gray-700" />
                    </div>
                    <p className="text-sm">No messages yet — say hello!</p>
                  </div>
                ) : (
                  (() => {
                    let lastDate = "";
                    return activeMessages.map((msg: SupportMessage) => {
                      const isAdminMsg = msg.sender_type === "admin";
                      const msgDate = new Date(msg.created_at).toDateString();
                      const showDate = msgDate !== lastDate;
                      lastDate = msgDate;
                      const isToday = msgDate === new Date().toDateString();
                      const dateLabel = isToday
                        ? "Today"
                        : new Date(msg.created_at).toLocaleDateString([], {
                            weekday: "short",
                            month: "short",
                            day: "numeric",
                          });
                      return (
                        <React.Fragment key={msg.id}>
                          {showDate && (
                            <div className="flex items-center gap-3 my-4">
                              <div className="flex-1 h-px bg-gray-800" />
                              <span className="text-xs text-gray-600 px-2">
                                {dateLabel}
                              </span>
                              <div className="flex-1 h-px bg-gray-800" />
                            </div>
                          )}
                          <div
                            className={`flex items-end gap-2 ${isAdminMsg ? "flex-row-reverse" : "flex-row"} mb-0.5`}
                          >
                            <div
                              className={`w-6 h-6 rounded-full flex-shrink-0 flex items-center justify-center text-xs font-bold ${isAdminMsg ? "bg-gradient-to-br from-orange-500 to-orange-600 text-black" : "bg-gray-800 text-gray-400"}`}
                            >
                              {isAdminMsg
                                ? "S"
                                : (adminActiveTicket.user?.username?.[0]?.toUpperCase() ??
                                  "U")}
                            </div>

                            <div
                              className={`flex flex-col gap-1 max-w-[72%] ${isAdminMsg ? "items-end" : "items-start"}`}
                            >
                              {msg.message && (
                                <div
                                  className={`px-3.5 py-2.5 text-sm leading-relaxed break-words ${
                                    isAdminMsg
                                      ? "bg-gradient-to-br from-orange-500 to-orange-600 text-white rounded-2xl rounded-br-md"
                                      : "bg-gray-800 text-gray-100 border border-gray-700/50 rounded-2xl rounded-bl-md"
                                  }`}
                                >
                                  {/* Translate ALL messages — both admin's own and the user's */}
                                  <TranslatedMessage message={msg.message} />
                                </div>
                              )}

                              {msg.attachment_url && (
                                <div className="mt-0.5">
                                  {msg.attachment_type === "image" ? (
                                    <a
                                      href={msg.attachment_url}
                                      target="_blank"
                                      rel="noreferrer"
                                    >
                                      <img
                                        src={msg.attachment_url}
                                        alt="attachment"
                                        className="max-w-[240px] rounded-xl border border-gray-700 hover:opacity-90 transition-opacity cursor-pointer"
                                      />
                                    </a>
                                  ) : (
                                    <a
                                      href={msg.attachment_url}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="flex items-center gap-2 px-3 py-2 bg-gray-800 border border-gray-700 rounded-xl text-xs text-gray-300 hover:bg-gray-700 transition-colors"
                                    >
                                      <DocumentIcon className="w-4 h-4 text-orange-400 flex-shrink-0" />
                                      <span className="truncate max-w-[180px]">
                                        {msg.attachment_name ?? "Download"}
                                      </span>
                                    </a>
                                  )}
                                </div>
                              )}

                              <span className="text-[10px] text-gray-600 px-1">
                                {formatMsgTime(msg.created_at)}
                              </span>
                            </div>
                          </div>
                        </React.Fragment>
                      );
                    });
                  })()
                )}

                {adminIsTyping &&
                  adminTypingUser &&
                  adminTypingTicketId === adminActiveTicket?.id && (
                    <div className="flex items-end gap-2 mt-2">
                      <div className="w-6 h-6 rounded-full bg-gray-800 flex items-center justify-center text-xs font-bold text-gray-400 flex-shrink-0">
                        {adminActiveTicket.user?.username?.[0]?.toUpperCase() ??
                          "U"}
                      </div>
                      <div className="px-3.5 py-2.5 bg-gray-800 border border-gray-700/50 rounded-2xl rounded-bl-md flex items-center gap-1.5">
                        <span className="text-xs text-gray-500">
                          {adminTypingUser}
                        </span>
                        <div className="flex gap-1 items-center ml-1">
                          {[0, 1, 2].map((i) => (
                            <span
                              key={i}
                              className="w-1.5 h-1.5 rounded-full bg-gray-500 animate-bounce"
                              style={{ animationDelay: `${i * 150}ms` }}
                            />
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                <div ref={messagesEndRef} />
              </div>

              {attachmentFile && (
                <div className="mx-4 mb-2 flex items-center gap-3 px-3 py-2.5 bg-gray-900 border border-gray-700 rounded-xl">
                  {attachmentPreview ? (
                    <img
                      src={attachmentPreview}
                      alt="preview"
                      className="w-12 h-12 object-cover rounded-lg border border-gray-700 flex-shrink-0"
                    />
                  ) : (
                    <div className="w-12 h-12 bg-gray-800 rounded-lg border border-gray-700 flex items-center justify-center flex-shrink-0">
                      <DocumentIcon className="w-5 h-5 text-orange-400" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-white truncate">
                      {attachmentFile.name}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {(attachmentFile.size / 1024).toFixed(1)} KB
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      setAttachmentFile(null);
                      setAttachmentPreview(null);
                      if (fileInputRef.current) fileInputRef.current.value = "";
                    }}
                    className="p-1.5 hover:bg-gray-700 rounded-lg text-gray-500 hover:text-white transition-colors flex-shrink-0"
                  >
                    <XMarkIcon className="w-4 h-4" />
                  </button>
                </div>
              )}

              {isClosed ? (
                <div className="px-4 py-3 pb-safe border-t border-gray-800 flex items-center justify-between gap-3 flex-shrink-0 pb-16 md:pb-3">
                  <p className="text-xs text-gray-500">
                    Ticket is{" "}
                    <span className="text-gray-400 font-medium">
                      {adminActiveTicket.status}
                    </span>
                    . Change status above to reply.
                  </p>
                  <button
                    onClick={() => handleStatusUpdate("in_progress")}
                    className="text-xs px-3 py-1.5 rounded-lg bg-orange-500/10 border border-orange-500/30 text-orange-400 hover:bg-orange-500/20 transition-colors flex-shrink-0"
                  >
                    Reopen
                  </button>
                </div>
              ) : (
                <div className="px-4 py-3 border-t border-gray-800 flex-shrink-0">
                  <div className="flex items-end gap-2 bg-gray-900 rounded-2xl border border-gray-700 focus-within:border-orange-500/40 transition-colors px-3 py-2">
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="p-1.5 rounded-lg hover:bg-gray-800 text-gray-500 hover:text-orange-400 transition-colors flex-shrink-0 mb-0.5"
                      title="Attach file"
                    >
                      <PaperClipIcon className="w-4 h-4" />
                    </button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*,.pdf,.doc,.docx,.txt"
                      className="hidden"
                      onChange={handleFileSelect}
                    />

                    <textarea
                      ref={textareaRef}
                      value={messageText}
                      onChange={(e) => {
                        setMessageText(e.target.value);
                        handleTyping();
                      }}
                      onKeyDown={handleKeyDown}
                      placeholder="Reply as Support..."
                      rows={1}
                      className="flex-1 bg-transparent text-sm text-white placeholder-gray-600 resize-none focus:outline-none py-1.5 max-h-32"
                      style={{ minHeight: "32px" }}
                    />

                    <button
                      onClick={handleSend}
                      disabled={
                        (!messageText.trim() && !attachmentFile) || isSending
                      }
                      className="p-2 rounded-xl bg-gradient-to-br from-orange-500 to-orange-600 text-white hover:from-orange-400 hover:to-orange-500 transition-all disabled:opacity-30 disabled:cursor-not-allowed flex-shrink-0 mb-0.5 shadow-md shadow-orange-900/40"
                    >
                      {isSending ? (
                        <ArrowPathIcon className="w-4 h-4 animate-spin" />
                      ) : (
                        <PaperAirplaneIcon className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                  <p className="text-[10px] text-gray-700 mt-1.5 px-1">
                    Enter to send · Shift+Enter for new line
                  </p>
                </div>
              )}
            </>
          )}
        </main>
      </div>

      <BottomNav />
    </div>
  );
};

export default AdminSupportManagement;
